/**
 * CommandPalette - Ctrl+K Quick Navigation
 * Epic 29: Story 29-5 - Command Palette integráció
 *
 * Uses cmdk library for keyboard-driven navigation
 * Integrates with favorites store and sidebar navigation
 */

import { useTranslation } from '@kgc/i18n';
import { selectSortedFavorites, useFavoritesStore } from '@kgc/ui';
import { Command } from 'cmdk';
import { Search, Star, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/** Navigation item structure — label/group store i18n KEYS, resolved at render */
interface NavItem {
  id: string;
  labelKey: string;
  href: string;
  groupKey: string;
  keywords?: string[];
  external?: boolean;
}

/** Flattened navigation items for search — all values are i18n keys */
const NAV_ITEMS: NavItem[] = [
  // Dashboard
  {
    id: 'dashboard',
    labelKey: 'nav.dashboard',
    href: '/dashboard',
    groupKey: 'nav.navigation',
    keywords: ['home', 'kezdo', 'dashboard'],
  },

  // Rental
  {
    id: 'rental',
    labelKey: 'nav.activeRentals',
    href: '/rental',
    groupKey: 'nav.rental',
    keywords: ['berles', 'aktiv', 'rental'],
  },
  {
    id: 'rental-new',
    labelKey: 'nav.newRental',
    href: '/rental/new',
    groupKey: 'nav.rental',
    keywords: ['uj', 'berles', 'new', 'rental'],
  },
  {
    id: 'rental-return',
    labelKey: 'nav.returnRental',
    href: '/rental/return',
    groupKey: 'nav.rental',
    keywords: ['vissza', 'lezaras', 'return'],
  },

  // Worksheet
  {
    id: 'worksheet',
    labelKey: 'nav.worksheets',
    href: '/worksheet',
    groupKey: 'nav.worksheet',
    keywords: ['munkalap', 'szerviz', 'worksheet'],
  },
  {
    id: 'worksheet-new',
    labelKey: 'nav.newWorksheet',
    href: '/worksheet/new',
    groupKey: 'nav.worksheet',
    keywords: ['uj', 'munkalap', 'new'],
  },

  // Sales
  {
    id: 'sales',
    labelKey: 'nav.salesList',
    href: '/sales',
    groupKey: 'nav.sales',
    keywords: ['eladas', 'sales'],
  },
  {
    id: 'sales-pos',
    labelKey: 'nav.pos',
    href: '/sales/new',
    groupKey: 'nav.sales',
    keywords: ['pos', 'penztar', 'kassza'],
  },

  // Inventory
  {
    id: 'inventory',
    labelKey: 'nav.stockList',
    href: '/inventory',
    groupKey: 'nav.inventory',
    keywords: ['keszlet', 'raktar', 'stock'],
  },
  {
    id: 'inventory-movements',
    labelKey: 'nav.movements',
    href: '/inventory/movements',
    groupKey: 'nav.inventory',
    keywords: ['mozgas', 'movements'],
  },
  {
    id: 'inventory-receive',
    labelKey: 'nav.goodsReceipt',
    href: '/goods-receipt',
    groupKey: 'nav.inventory',
    keywords: ['bevetelez', 'receipt'],
  },

  // Master Data
  {
    id: 'products',
    labelKey: 'nav.products',
    href: '/products',
    groupKey: 'nav.masterData',
    keywords: ['cikk', 'termek', 'product'],
  },
  {
    id: 'quotations',
    labelKey: 'nav.quotes',
    href: '/quotations',
    groupKey: 'nav.masterData',
    keywords: ['arajanl', 'quote'],
  },
  {
    id: 'contracts',
    labelKey: 'nav.contracts',
    href: '/contracts',
    groupKey: 'nav.masterData',
    keywords: ['szerzodes', 'contract'],
  },
  {
    id: 'invoices',
    labelKey: 'nav.invoices',
    href: '/invoices',
    groupKey: 'nav.masterData',
    keywords: ['szamla', 'invoice'],
  },

  // Partners
  {
    id: 'partners',
    labelKey: 'nav.partnerList',
    href: '/partners',
    groupKey: 'nav.partners',
    keywords: ['partner', 'ugyfel', 'vevo'],
  },
  {
    id: 'partners-new',
    labelKey: 'nav.newPartner',
    href: '/partners/new',
    groupKey: 'nav.partners',
    keywords: ['uj', 'partner', 'new'],
  },

  // Vehicles
  {
    id: 'vehicles-rental',
    labelKey: 'nav.rentalVehicles',
    href: '/vehicles/rental',
    groupKey: 'nav.vehicles',
    keywords: ['jarmu', 'bergep', 'auto', 'vehicle'],
  },
  {
    id: 'vehicles-company',
    labelKey: 'nav.companyVehicles',
    href: '/vehicles/company',
    groupKey: 'nav.vehicles',
    keywords: ['ceges', 'jarmu', 'company'],
  },
  {
    id: 'vehicles-expiring',
    labelKey: 'nav.expiringDocuments',
    href: '/vehicles/expiring',
    groupKey: 'nav.vehicles',
    keywords: ['lejaro', 'muszaki', 'expiring'],
  },

  // Chat & Reports
  {
    id: 'chat',
    labelKey: 'nav.chat',
    href: '/chat',
    groupKey: 'nav.chat',
    keywords: ['chat', 'uzenet', 'message'],
  },
  {
    id: 'reports',
    labelKey: 'nav.reports',
    href: '/reports',
    groupKey: 'nav.reports',
    keywords: ['riport', 'report', 'statisztika'],
  },
  {
    id: 'tasks',
    labelKey: 'nav.tasks',
    href: '/tasks',
    groupKey: 'nav.tasks',
    keywords: ['feladat', 'task', 'todo'],
  },

  // Integrations
  {
    id: 'integrations-crm',
    labelKey: 'nav.crm',
    href: '/integrations/crm',
    groupKey: 'nav.integrations',
    keywords: ['crm', 'twenty'],
  },
  // Admin
  {
    id: 'users',
    labelKey: 'nav.users',
    href: '/users',
    groupKey: 'nav.admin',
    keywords: ['user', 'felhasznalo'],
  },
  {
    id: 'users-roles',
    labelKey: 'nav.permissions',
    href: '/users/roles',
    groupKey: 'nav.admin',
    keywords: ['role', 'jogosultsag', 'rbac'],
  },
  {
    id: 'tenant',
    labelKey: 'nav.tenantAdmin',
    href: '/tenant',
    groupKey: 'nav.admin',
    keywords: ['tenant', 'uzlet'],
  },
  {
    id: 'feature-flags',
    labelKey: 'nav.featureFlags',
    href: '/feature-flags',
    groupKey: 'nav.admin',
    keywords: ['feature', 'flag'],
  },

  // Settings
  {
    id: 'settings',
    labelKey: 'nav.settings',
    href: '/settings',
    groupKey: 'nav.settings',
    keywords: ['beallitas', 'config', 'settings'],
  },
  {
    id: 'myforgeos',
    labelKey: 'MyForgeOS',
    href: '/myforgeos',
    groupKey: 'nav.settings',
    keywords: ['forge', 'os'],
  },
];

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const favorites = useFavoritesStore(selectSortedFavorites);
  const { addFavorite, removeFavorite, isFavorite } = useFavoritesStore();

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  // Reset search when closing
  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const handleSelect = useCallback(
    (href: string, external?: boolean) => {
      if (external) {
        window.open(href, '_blank', 'noopener,noreferrer');
      } else {
        navigate(href);
      }
      onOpenChange(false);
    },
    [navigate, onOpenChange]
  );

  const toggleFavorite = useCallback(
    (e: React.MouseEvent, itemId: string, label: string) => {
      e.stopPropagation();
      if (isFavorite(itemId)) {
        removeFavorite(itemId);
      } else {
        addFavorite(itemId, label);
      }
    },
    [addFavorite, removeFavorite, isFavorite]
  );

  // Get favorite items
  const favoriteItems = favorites
    .map(fav => {
      const navItem = NAV_ITEMS.find(item => item.id === fav.menuItemId);
      return navItem ? { ...navItem, favoriteLabel: fav.label } : null;
    })
    .filter((item): item is NavItem & { favoriteLabel: string | undefined } => item !== null);

  // Group regular items by translated group name
  const groupedItems = useMemo(() => {
    return NAV_ITEMS.reduce(
      (acc, item) => {
        const groupLabel = t(item.groupKey);
        if (!acc[groupLabel]) {
          acc[groupLabel] = [];
        }
        acc[groupLabel].push(item);
        return acc;
      },
      {} as Record<string, NavItem[]>
    );
  }, [t]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
      />

      {/* Command Dialog */}
      <div className="absolute left-1/2 top-[20%] w-full max-w-lg -translate-x-1/2">
        <Command
          className="overflow-hidden rounded-lg border border-border/20 bg-card shadow-2xl glass-overlay"
          loop
        >
          {/* Search Input */}
          <div className="flex items-center border-b border-white/10 px-4">
            <Search className="mr-2 h-4 w-4 shrink-0 text-white/50" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder={t('command.searchPlaceholder')}
              className="flex h-12 w-full bg-transparent text-white placeholder:text-white/40 outline-none"
            />
            <button
              onClick={() => onOpenChange(false)}
              className="ml-2 rounded p-1 text-white/50 hover:bg-white/10 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Results */}
          <Command.List className="max-h-[400px] overflow-y-auto p-2">
            <Command.Empty className="py-6 text-center text-sm text-white/50">
              {t('command.noResults')}
            </Command.Empty>

            {/* Favorites Group */}
            {favoriteItems.length > 0 && (
              <Command.Group heading={t('nav.favorites')} className="mb-2">
                <div className="px-2 py-1.5 text-xs font-semibold text-primary">
                  {t('nav.favorites')}
                </div>
                {favoriteItems.map(item => (
                  <Command.Item
                    key={`fav-${item.id}`}
                    value={`fav-${t(item.labelKey)} ${item.favoriteLabel ?? ''}`}
                    onSelect={() => handleSelect(item.href, item.external)}
                    className="group flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-white/80 outline-none data-[selected=true]:bg-white/10 data-[selected=true]:text-white"
                  >
                    <Star className="h-4 w-4 fill-primary text-primary" />
                    <span className="flex-1">{item.favoriteLabel ?? t(item.labelKey)}</span>
                    <span className="text-xs text-white/30">{t(item.groupKey)}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* All Navigation Groups */}
            {Object.entries(groupedItems).map(([group, items]) => (
              <Command.Group key={group} heading={group}>
                <div className="px-2 py-1.5 text-xs font-semibold text-white/40">{group}</div>
                {items.map(item => (
                  <Command.Item
                    key={item.id}
                    value={`${t(item.labelKey)} ${item.keywords?.join(' ') ?? ''} ${group}`}
                    onSelect={() => handleSelect(item.href, item.external)}
                    className="group flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-white/80 outline-none data-[selected=true]:bg-white/10 data-[selected=true]:text-white"
                  >
                    <span className="flex-1">{t(item.labelKey)}</span>
                    <button
                      onClick={e => toggleFavorite(e, item.id, t(item.labelKey))}
                      className={`rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 ${
                        isFavorite(item.id) ? 'text-primary' : 'text-white/30 hover:text-white/60'
                      }`}
                      title={
                        isFavorite(item.id) ? t('command.removeFavorite') : t('command.addFavorite')
                      }
                    >
                      <Star
                        className={`h-3.5 w-3.5 ${isFavorite(item.id) ? 'fill-current' : ''}`}
                      />
                    </button>
                    {item.external && <span className="text-xs text-white/30">↗</span>}
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </Command.List>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-white/10 px-4 py-2 text-xs text-white/40">
            <div className="flex items-center gap-4">
              <span>
                <kbd className="rounded bg-white/10 px-1.5 py-0.5">↑↓</kbd> {t('command.navigate')}
              </span>
              <span>
                <kbd className="rounded bg-white/10 px-1.5 py-0.5">Enter</kbd> {t('command.open')}
              </span>
              <span>
                <kbd className="rounded bg-white/10 px-1.5 py-0.5">Esc</kbd> {t('command.close')}
              </span>
            </div>
            <span>Ctrl+K</span>
          </div>
        </Command>
      </div>
    </div>
  );
}
